import React from "react";
import { Col, Row, UiButton, UiCheckbox, UiDivider, UiTabPanel, UiText } from "~/components/elements";
import { useAppDispatch, useAppSelector } from "~/hooks";
import { getHeadshotsFavoriteTab, selectImagesByGroup, setSelectAllCheckboxes } from "~/redux/dashboards/slice";
import { DownloadIcon, ShareIcon } from "~/components/icons/SvgIcons";
import ResultGallery from "~/pages/result-detail/components/ResultGallery";
import { ResultImageEntity } from "~/types/models/dashboard";

interface FavoriteHeadshotsTabPanelProps {
  selectAllFavoriteCheckboxValue: boolean;
  headshotsCount: { all: number; favourite: number };
  downloadLoading: boolean;
  onHandleDownloadImages: () => void;
  onClickShare: () => void;
}

export const FavoriteHeadshotsTabPanel: React.FC<FavoriteHeadshotsTabPanelProps> = ({
  selectAllFavoriteCheckboxValue,
  headshotsCount,
  downloadLoading,
  onHandleDownloadImages,
  onClickShare,
}) => {
  const dispatch = useAppDispatch();
  const resultHeadshotsGroupByFavorite: Record<string, ResultImageEntity[]> = useAppSelector(state =>
    getHeadshotsFavoriteTab(state, { styleIds: [] })
  );

  const onClickSelectAllFavorite = (checked: boolean) => {
    dispatch(selectImagesByGroup({ group: "favorite", isSelected: checked }));
    dispatch(setSelectAllCheckboxes({ group: "favorite", isSelected: checked }));
  };

  return (
    <UiTabPanel>
      <Row justifyContent="space-between" align="center" mt={1}>
        <UiCheckbox
          isChecked={selectAllFavoriteCheckboxValue}
          onChange={e => onClickSelectAllFavorite(e.target.checked)}
        >
          <UiText vr="title1" whiteSpace="nowrap">
            Your favorite photoshoots ({headshotsCount.favourite})
          </UiText>
        </UiCheckbox>
        <Col
          flexDirection={{ base: "column", md: "row" }}
          gap={2}
          align="center"
          justifyContent={{ base: "center", md: "flex-end" }}
          flexWrap="wrap"
          pos={{ base: "fixed", md: "inherit" }}
          bottom={0}
          left={0}
          right={0}
          bg={{ base: "white", md: "transparent" }}
          zIndex="3"
          pt={{ base: 4, md: 0 }}
          pb={{ base: 5, md: 0 }}
          px={4}
        >
          <UiButton
            size="sm"
            w={{ base: "full", md: "auto" }}
            variant="outline"
            leftIcon={<DownloadIcon boxSize={6} dColor="#0B0D2B" />}
            borderColor="darkblue"
            bg="transparent"
            color="darkblue"
            _hover={{
              bg: "transparent",
              color: "darkblue",
              cursor: headshotsCount.favourite > 0 ? "pointer" : "not-allowed",
            }}
            onClick={onHandleDownloadImages}
            isLoading={downloadLoading}
            display={{
              base: headshotsCount.favourite > 0 ? "inline-flex" : "none",
              md: "inline-flex",
            }}
          >
            Download Favorites
          </UiButton>
          <UiButton
            size="sm"
            w={{ base: "full", md: "auto" }}
            variant="outline"
            leftIcon={<ShareIcon boxSize={6} dColor="#FFFFFF" />}
            bg="darkblue"
            color="neutral.gray00"
            _hover={{
              bg: "darkblue",
              color: "neutral.gray00",
              cursor: headshotsCount?.favourite ? "pointer" : "not-allowed",
            }}
            onClick={onClickShare}
          >
            Share Favorites
          </UiButton>
        </Col>
      </Row>
      <Col mt={5}>
        <ResultGallery galleryImages={resultHeadshotsGroupByFavorite.favorited} />
        <UiDivider text="Your other photoshoots" containerProps={{ my: 4 }} />
        <ResultGallery galleryImages={resultHeadshotsGroupByFavorite.others} />
      </Col>
    </UiTabPanel>
  );
};
